import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import Register from './components/Register';
import katex from 'katex';
import 'katex/dist/katex.min.css';

// Render LaTeX string to HTML via KaTeX
const renderLatex = (latex: string, displayMode = false): string => {
  try {
    return katex.renderToString(latex, { throwOnError: false, displayMode });
  } catch {
    return latex;
  }
};

const Latex: React.FC<{ math: string; display?: boolean; className?: string }> = ({ math, display = false, className = '' }) => (
  <span className={className} dangerouslySetInnerHTML={{ __html: renderLatex(math, display) }} />
);

interface UserData {
  id: string;
  username: string;
  email: string;
  wallet: { ahn: number; urja: number; };
}

interface Course {
  id: string;
  title: string;
  description: string;
  difficulty: string;
  lesson_count: string;
}

interface Lesson {
  id: string;
  lesson_number: number;
  title: string;
  content?: string;
  course_title?: string;
  course_id?: string;
  quiz?: Quiz | null;
  prevLesson?: { id: string; title: string } | null;
  nextLesson?: { id: string; title: string } | null;
}

interface Quiz {
  id: string;
  questions: QuizQuestion[];
  passing_score: number;
}

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
}

interface Progress {
  lesson_id: string;
  completed: boolean;
}

interface Certification {
  id: string;
  course_id: string;
  title: string;
  description: string;
  price_ahn: number;
  course_title: string;
  difficulty: string;
}

interface UserCertification {
  id: string;
  title: string;
  course_title: string;
  certificate_number: string;
  earned_at: string;
}

interface Publication {
  id: string;
  title: string;
  authors: string;
  venue: string;
  publication_date: string;
  doi?: string;
  abstract: string;
  pdf_url?: string;
  citation_count: number;
  tags: string[];
  is_featured: boolean;
}

interface PublicationStats {
  total_publications: string;
  total_citations: string;
}

// Journal system interfaces
interface JournalSection {
  id: string;
  name: string;
  description: string;
}

interface Manuscript {
  id: string;
  manuscript_number: string;
  title: string;
  abstract: string;
  keywords: string[];
  section_id: string;
  section_name: string;
  status: string;
  manuscript_type: string;
  submission_date: string;
  review_count: number;
  authors?: ManuscriptAuthor[];
  files?: ManuscriptFile[];
}

interface ManuscriptAuthor {
  id?: string;
  first_name: string;
  last_name: string;
  email: string;
  affiliation: string;
  department: string;
  country: string;
  orcid: string;
  is_corresponding: boolean;
  contribution_statement: string;
  author_order: number;
}

interface ManuscriptFile {
  id: string;
  file_type: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  caption: string;
  uploaded_at: string;
}

type View = 'home' | 'publications' | 'courses' | 'course' | 'lesson' | 'certifications' | 'journal' | 'journal-submit' | 'my-manuscripts' | 'login' | 'tokens' | 'token-detail' | 'my-knowledge' | 'reactions';

// Knowledge Token interfaces
interface KnowledgeToken {
  id: string;
  symbol: string;
  title: string;
  description: string;
  domain: string;
  tier: number;
  mass: string;
  urja_reward: string;
  content: {
    equation?: string;
    explanation?: string;
    key_insight?: string;
    derivation?: string;
    units?: string;
    constants?: string;
    rules?: string;
    forms?: string;
    identities?: string;
    methods?: string;
    applications?: string;
  };
  prerequisites: string[];
  prerequisite_details?: { id: string; symbol: string; title: string; domain: string; tier: number; mass: string }[];
  quiz_schema: { question: string; options: string[]; correctAnswer: number }[];
  acquired_by_count?: number;
  is_active: boolean;
}

interface LearnerToken {
  id: string;
  token_id: string;
  symbol: string;
  title: string;
  description: string;
  domain: string;
  tier: number;
  mass: string;
  urja_reward: string;
  acquired_at: string;
  acquisition_method: string;
  quiz_score: number;
}

interface DomainMastery {
  domain: string;
  tier_1_count: number;
  tier_2_count: number;
  tier_3_count: number;
  tier_4_count: number;
  tier_5_count: number;
  total_tokens: number;
  total_mass: string;
  mastery_level: string;
}

interface KnowledgeReaction {
  id: string;
  name: string;
  description: string;
  input_tokens: string[];
  input_details: { id: string; symbol: string; title: string; tier: number }[];
  output_token_id: string;
  output_symbol: string;
  output_title: string;
  output_tier: number;
  urja_cost: string;
  urja_reward: string;
  requires_tier: number;
  can_execute?: boolean;
  has_all_inputs?: boolean;
  already_have_output?: boolean;
}

interface TokenDomain {
  domain: string;
  token_count: string;
  min_tier: number;
  max_tier: number;
}

const AppContent: React.FC = () => {
  const { isAuthenticated, user: authUser, token, logout } = useAuth();
  const [showRegister, setShowRegister] = useState(false);

  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // View state
  const [view, setView] = useState<View>('home');

  // Course state
  const [courses, setCourses] = useState<Course[]>([]);
  const [currentCourse, setCurrentCourse] = useState<Course & { lessons: Lesson[] } | null>(null);
  const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null);
  const [progress, setProgress] = useState<Progress[]>([]);

  // Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<{[key: number]: number}>({});
  const [quizResult, setQuizResult] = useState<{score: number; passed: boolean; total: number} | null>(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);

  // Certification state
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [userCertifications, setUserCertifications] = useState<UserCertification[]>([]);
  const [purchasingCert, setPurchasingCert] = useState<string | null>(null);

  // Publications state
  const [publications, setPublications] = useState<Publication[]>([]);
  const [publicationStats, setPublicationStats] = useState<PublicationStats | null>(null);

  // Journal system state
  const [journalSections, setJournalSections] = useState<JournalSection[]>([]);
  const [manuscripts, setManuscripts] = useState<Manuscript[]>([]);
  const [currentManuscript, setCurrentManuscript] = useState<Manuscript | null>(null);
  const [submissionStep, setSubmissionStep] = useState(1);
  const [manuscriptDraft, setManuscriptDraft] = useState<Partial<Manuscript>>({
    title: '',
    abstract: '',
    keywords: [],
    manuscript_type: 'research-article'
  });
  const [draftAuthors, setDraftAuthors] = useState<ManuscriptAuthor[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Knowledge Token state
  const [knowledgeTokens, setKnowledgeTokens] = useState<KnowledgeToken[]>([]);
  const [tokenDomains, setTokenDomains] = useState<TokenDomain[]>([]);
  const [selectedTokenDomain, setSelectedTokenDomain] = useState<string>('all');
  const [selectedTokenTier, setSelectedTokenTier] = useState<number>(0);
  const [currentToken, setCurrentToken] = useState<KnowledgeToken | null>(null);
  const [myTokens, setMyTokens] = useState<LearnerToken[]>([]);
  const [domainMastery, setDomainMastery] = useState<DomainMastery[]>([]);
  const [masteryTotals, setMasteryTotals] = useState<{tokens_acquired: number; total_mass: number; tokens_available: number; mass_available: number} | null>(null);
  const [knowledgeReactions, setKnowledgeReactions] = useState<KnowledgeReaction[]>([]);
  const [tokenQuizAnswers, setTokenQuizAnswers] = useState<{[key: number]: number}>({});
  const [tokenQuizResult, setTokenQuizResult] = useState<{success: boolean; acquired: boolean; score: number; correct: number; total: number; urja_earned?: number; message: string} | null>(null);
  const [acquiringToken, setAcquiringToken] = useState(false);
  const [executingReaction, setExecutingReaction] = useState<string | null>(null);

  // All hooks must be at the top before any returns
  useEffect(() => {
    if (isAuthenticated && token) {
      fetch('/api/me', { headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => setUser(data))
        .catch(err => setError(err instanceof Error ? err.message : 'Error'))
        .finally(() => setLoading(false));

      fetch('/api/courses')
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => setCourses(data))
        .catch(err => console.error('Error fetching courses:', err));

      fetch('/api/certifications')
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => setCertifications(data))
        .catch(err => console.error('Error fetching certifications:', err));

      fetch('/api/certifications/user', { headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => setUserCertifications(data))
        .catch(err => console.error('Error fetching user certifications:', err));

      fetch('/api/publications')
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => setPublications(data))
        .catch(err => console.error('Error fetching publications:', err));

      // Fetch knowledge tokens
      fetch('/api/tokens')
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => setKnowledgeTokens(data))
        .catch(err => console.error('Error fetching knowledge tokens:', err));

      fetch('/api/tokens/domains')
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => setTokenDomains(data))
        .catch(err => console.error('Error fetching token domains:', err));

      // Fetch user's tokens and mastery
      fetch('/api/my-tokens', { headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => setMyTokens(data))
        .catch(err => console.error('Error fetching my tokens:', err));

      fetch('/api/mastery', { headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => { setDomainMastery(data.domains); setMasteryTotals(data.totals); })
        .catch(err => console.error('Error fetching mastery:', err));

    } else if (!isAuthenticated) {
      setLoading(false);
      // Fetch public data even when not logged in
      fetch('/api/tokens')
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => setKnowledgeTokens(data))
        .catch(err => console.error('Error fetching knowledge tokens:', err));

      fetch('/api/tokens/domains')
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => setTokenDomains(data))
        .catch(err => console.error('Error fetching token domains:', err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token]);

  // Reset view to home after successful login
  useEffect(() => {
    if (isAuthenticated && view === 'login') {
      setView('home');
    }
  }, [isAuthenticated, view]);

  const fetchUserData = async () => {
    try {
      if (!token) {
        setLoading(false);
        return;
      }
      const response = await fetch('/api/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Authentication failed');
      setUser(await response.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await fetch('/api/courses');
      if (response.ok) setCourses(await response.json());
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const fetchCertifications = async () => {
    try {
      const response = await fetch('/api/certifications');
      if (response.ok) setCertifications(await response.json());

      if (token) {
        const userCertsResponse = await fetch('/api/certifications/user', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (userCertsResponse.ok) setUserCertifications(await userCertsResponse.json());
      }
    } catch (err) {
      console.error('Error fetching certifications:', err);
    }
  };

  const fetchPublications = async () => {
    try {
      const response = await fetch('/api/publications');
      if (response.ok) setPublications(await response.json());

      const statsResponse = await fetch('/api/publications/stats');
      if (statsResponse.ok) setPublicationStats(await statsResponse.json());
    } catch (err) {
      console.error('Error fetching publications:', err);
    }
  };

  // Journal system functions
  const fetchJournalSections = async () => {
    try {
      const response = await fetch('/api/journal/sections');
      if (response.ok) setJournalSections(await response.json());
    } catch (err) {
      console.error('Error fetching journal sections:', err);
    }
  };

  const fetchMyManuscripts = async () => {
    if (!token) return;
    try {
      const response = await fetch('/api/journal/my-manuscripts', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setManuscripts(await response.json());
    } catch (err) {
      console.error('Error fetching manuscripts:', err);
    }
  };

  // Knowledge Token functions
  const fetchTokenDetail = async (tokenId: string) => {
    try {
      const response = await fetch(`/api/tokens/${tokenId}`);
      if (response.ok) {
        const data = await response.json();
        setCurrentToken(data);
        setTokenQuizAnswers({});
        setTokenQuizResult(null);
        setView('token-detail');
      }
    } catch (err) {
      console.error('Error fetching token detail:', err);
    }
  };

  const fetchMyTokens = async () => {
    if (!token) return;
    try {
      const response = await fetch('/api/my-tokens', { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setMyTokens(await response.json());
    } catch (err) {
      console.error('Error fetching my tokens:', err);
    }
  };

  const fetchMastery = async () => {
    if (!token) return;
    try {
      const response = await fetch('/api/mastery', { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        setDomainMastery(data.domains);
        setMasteryTotals(data.totals);
      }
    } catch (err) {
      console.error('Error fetching mastery:', err);
    }
  };

  const fetchReactions = async () => {
    if (!token) return;
    try {
      const response = await fetch('/api/knowledge-reactions/available', { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setKnowledgeReactions(await response.json());
    } catch (err) {
      console.error('Error fetching reactions:', err);
    }
  };

  const handleAcquireToken = async (tokenId: string) => {
    if (!token) return;
    setAcquiringToken(true);
    try {
      const response = await fetch(`/api/tokens/${tokenId}/acquire`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: tokenQuizAnswers })
      });
      const data = await response.json();
      setTokenQuizResult(data);
      if (data.acquired) {
        fetchMyTokens();
        fetchMastery();
        // Refresh user data to show updated URJA
        fetch('/api/me', { headers: { 'Authorization': `Bearer ${token}` } })
          .then(res => res.ok ? res.json() : Promise.reject())
          .then(d => setUser(d));
      }
    } catch (err) {
      console.error('Error acquiring token:', err);
    } finally {
      setAcquiringToken(false);
    }
  };

  const handleExecuteReaction = async (reactionId: string) => {
    if (!token) return;
    setExecutingReaction(reactionId);
    try {
      const response = await fetch(`/api/knowledge-reactions/${reactionId}/execute`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        fetchMyTokens();
        fetchMastery();
        fetchReactions();
        fetch('/api/me', { headers: { 'Authorization': `Bearer ${token}` } })
          .then(res => res.ok ? res.json() : Promise.reject())
          .then(d => setUser(d));
        alert(`Knowledge fusion complete: ${data.output_token.symbol} — ${data.output_token.title}\nNet URJA: ${data.net_urja > 0 ? '+' : ''}${data.net_urja}`);
      } else {
        alert(data.error || 'Reaction failed');
      }
    } catch (err) {
      console.error('Error executing reaction:', err);
    } finally {
      setExecutingReaction(null);
    }
  };

  const isTokenAcquired = (tokenId: string) => myTokens.some(t => t.token_id === tokenId);

  const tierColors: {[key: number]: string} = {
    1: 'from-emerald-500 to-green-600',
    2: 'from-blue-500 to-indigo-600',
    3: 'from-purple-500 to-violet-600',
    4: 'from-orange-500 to-red-600',
    5: 'from-pink-500 to-rose-600'
  };

  const tierNames: {[key: number]: string} = {
    1: 'Fundamental',
    2: 'Composite',
    3: 'Fused',
    4: 'Heavy',
    5: 'Exotic'
  };

  const domainColors: {[key: string]: string} = {
    physics: 'text-blue-600 bg-blue-50 border-blue-200',
    mathematics: 'text-purple-600 bg-purple-50 border-purple-200',
    chemistry: 'text-green-600 bg-green-50 border-green-200',
    computing: 'text-orange-600 bg-orange-50 border-orange-200'
  };

  const startNewSubmission = async () => {
    await fetchJournalSections();
    setSubmissionStep(1);
    setManuscriptDraft({
      title: '',
      abstract: '',
      keywords: [],
      manuscript_type: 'research-article'
    });
    setDraftAuthors([]);
    setCurrentManuscript(null);
    setView('journal-submit');
  };

  const saveManuscriptDraft = async () => {
    if (!token) return;
    try {
      const response = await fetch('/api/journal/manuscripts', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(manuscriptDraft)
      });
      if (response.ok) {
        const manuscript = await response.json();
        setCurrentManuscript(manuscript);
        alert('Manuscript draft saved successfully! MS#: ' + manuscript.manuscript_number);
        return manuscript;
      } else {
        const error = await response.json();
        alert('Error: ' + error.error);
      }
    } catch (err) {
      console.error('Error saving manuscript:', err);
    }
  };

  const addAuthorToManuscript = async (author: ManuscriptAuthor) => {
    if (!token || !currentManuscript) return;
    try {
      const response = await fetch(`/gyanodbhaw/api/journal/manuscripts/${currentManuscript.id}/authors`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(author)
      });
      if (response.ok) {
        alert('Author added successfully!');
        return true;
      }
    } catch (err) {
      console.error('Error adding author:', err);
    }
    return false;
  };

  const uploadManuscriptFile = async (file: File, fileType: string) => {
    if (!token || !currentManuscript) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileType', fileType);
      formData.append('caption', file.name);
      formData.append('fileOrder', '1');

      const response = await fetch(`/gyanodbhaw/api/journal/manuscripts/${currentManuscript.id}/files`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        alert('File uploaded successfully!');
        return true;
      } else {
        const error = await response.json();
        alert('Upload failed: ' + error.error);
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      alert('Upload failed');
    } finally {
      setUploadingFile(false);
    }
    return false;
  };

  const submitManuscriptForReview = async () => {
    if (!token || !currentManuscript) return;
    try {
      const response = await fetch(`/gyanodbhaw/api/journal/manuscripts/${currentManuscript.id}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        alert('Manuscript submitted successfully! You will be notified about editorial decisions.');
        setView('my-manuscripts');
        fetchMyManuscripts();
      } else {
        const error = await response.json();
        alert('Submission failed: ' + error.error);
      }
    } catch (err) {
      console.error('Error submitting manuscript:', err);
    }
  };

  const purchaseCertification = async (certId: string) => {
    if (!token) return;
    setPurchasingCert(certId);
    try {
      const response = await fetch(`/gyanodbhaw/api/certifications/${certId}/purchase`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        alert(`Congratulations! You earned: ${data.title}\nCertificate #: ${data.certificateNumber}`);
        fetchCertifications();
        fetchUserData();
      } else {
        alert(data.message || data.error);
      }
    } catch (err) {
      console.error('Error purchasing certification:', err);
    } finally {
      setPurchasingCert(null);
    }
  };

  const hasCertification = (certId: string) => userCertifications.some(uc => uc.id === certId);

  const openCourse = async (courseId: string) => {
    try {
      const response = await fetch(`/gyanodbhaw/api/courses/${courseId}`);
      if (response.ok) {
        const course = await response.json();
        setCurrentCourse(course);
        setView('course');
        if (token) {
          const progResponse = await fetch(`/gyanodbhaw/api/courses/${courseId}/progress`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (progResponse.ok) setProgress(await progResponse.json());
        }
      }
    } catch (err) {
      console.error('Error fetching course:', err);
    }
  };

  const openLesson = async (lessonId: string) => {
    try {
      const response = await fetch(`/gyanodbhaw/api/lessons/${lessonId}`);
      if (response.ok) {
        const lesson = await response.json();
        setCurrentLesson(lesson);
        setView('lesson');
        setShowQuiz(false);
        setQuizAnswers({});
        setQuizResult(null);
      }
    } catch (err) {
      console.error('Error fetching lesson:', err);
    }
  };

  const markComplete = async () => {
    if (!currentLesson || !token) return;
    try {
      await fetch(`/gyanodbhaw/api/lessons/${currentLesson.id}/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setProgress([...progress, { lesson_id: currentLesson.id, completed: true }]);
    } catch (err) {
      console.error('Error marking complete:', err);
    }
  };

  const submitQuiz = async () => {
    if (!currentLesson?.quiz || !token) return;
    setSubmittingQuiz(true);
    try {
      const response = await fetch(`/gyanodbhaw/api/quizzes/${currentLesson.quiz.id}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ answers: quizAnswers })
      });
      if (response.ok) {
        const result = await response.json();
        setQuizResult(result);
        if (result.passed) {
          setProgress([...progress, { lesson_id: currentLesson.id, completed: true }]);
        }
      }
    } catch (err) {
      console.error('Error submitting quiz:', err);
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const isLessonComplete = (lessonId: string) => progress.some(p => p.lesson_id === lessonId && p.completed);

  const renderContent = (content: string) => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('# ')) return <h1 key={i} className="text-3xl font-bold text-gray-900 mt-6 mb-4">{line.slice(2)}</h1>;
      if (line.startsWith('## ')) return <h2 key={i} className="text-2xl font-bold text-gray-800 mt-5 mb-3">{line.slice(3)}</h2>;
      if (line.startsWith('### ')) return <h3 key={i} className="text-xl font-semibold text-gray-700 mt-4 mb-2">{line.slice(4)}</h3>;
      if (line.startsWith('```python')) return <pre key={i} className="bg-gray-900 p-4 rounded-lg mt-2 overflow-x-auto"><code className="text-green-400">{''}</code></pre>;
      if (line.startsWith('```')) {
        if (line === '```') return null;
        return <pre key={i} className="bg-gray-800 p-3 rounded mt-2 text-gray-300 overflow-x-auto">{line.slice(3)}</pre>;
      }
      if (line.startsWith('- **')) {
        const match = line.match(/- \*\*(.+?)\*\*:?\s*(.*)$/);
        if (match) return <li key={i} className="text-gray-700 ml-4 my-1"><strong className="text-indigo-600">{match[1]}</strong>{match[2] ? `: ${match[2]}` : ''}</li>;
      }
      if (line.startsWith('- ')) return <li key={i} className="text-gray-600 ml-4 my-1">{line.slice(2)}</li>;
      if (line.match(/^\d+\./)) return <li key={i} className="text-gray-600 ml-4 my-1">{line}</li>;
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="text-gray-700 my-2">{line}</p>;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white border-2 border-red-200 p-8 rounded-2xl max-w-md text-center shadow-xl">
          <div className="text-5xl mb-4">🔒</div>
          <h2 className="text-red-600 text-xl font-bold mb-4">Authentication Required</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <a href="/" className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-xl inline-block font-semibold transition-all">Go to Login</a>
        </div>
      </div>
    );
  }

  // ============================================
  // LESSON VIEW
  // ============================================
  if (view === 'lesson' && currentLesson) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => { setView('course'); setShowQuiz(false); setQuizResult(null); }}
            className="text-indigo-600 hover:text-indigo-800 mb-6 flex items-center gap-2 font-semibold transition-colors">
            <span>←</span> Back to {currentLesson.course_title}
          </button>

          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-indigo-500 text-sm font-semibold">Lesson {currentLesson.lesson_number}</span>
                <h1 className="text-3xl font-bold text-gray-900 mt-1">{currentLesson.title}</h1>
              </div>
              {isLessonComplete(currentLesson.id) && (
                <span className="bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold">✓ Completed</span>
              )}
            </div>
          </div>

          {!showQuiz ? (
            <>
              <div className="bg-white rounded-2xl border border-gray-200 p-8 mb-6 shadow-lg prose max-w-none">
                {currentLesson.content && renderContent(currentLesson.content)}
              </div>

              <div className="flex flex-wrap gap-4 justify-between items-center">
                <div className="flex gap-4">
                  {currentLesson.prevLesson && (
                    <button onClick={() => openLesson(currentLesson.prevLesson!.id)}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-xl font-semibold transition-all">
                      ← Previous
                    </button>
                  )}
                  {currentLesson.nextLesson && (
                    <button onClick={() => openLesson(currentLesson.nextLesson!.id)}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-xl font-semibold transition-all">
                      Next →
                    </button>
                  )}
                </div>

                {currentLesson.quiz ? (
                  <button onClick={() => setShowQuiz(true)}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-3 rounded-xl font-bold shadow-lg transition-all">
                    Take Quiz
                  </button>
                ) : !isLessonComplete(currentLesson.id) && (
                  <button onClick={markComplete}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-8 py-3 rounded-xl font-bold shadow-lg transition-all">
                    Mark Complete
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Quiz</h2>

              {!quizResult ? (
                <>
                  {currentLesson.quiz?.questions.map((q, qIdx) => (
                    <div key={qIdx} className="mb-6 bg-gray-50 p-6 rounded-xl">
                      <p className="text-gray-900 font-semibold mb-4">{qIdx + 1}. {q.question}</p>
                      <div className="space-y-3">
                        {q.options.map((opt, oIdx) => (
                          <label key={oIdx}
                            className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer transition-all ${
                              quizAnswers[qIdx] === oIdx
                                ? 'bg-indigo-50 border-2 border-indigo-500'
                                : 'bg-white border-2 border-gray-200 hover:border-gray-300'
                            }`}>
                            <input type="radio" name={`q${qIdx}`} checked={quizAnswers[qIdx] === oIdx}
                              onChange={() => setQuizAnswers({...quizAnswers, [qIdx]: oIdx})}
                              className="w-5 h-5 text-indigo-600" />
                            <span className="text-gray-800">{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="flex gap-4">
                    <button onClick={() => setShowQuiz(false)}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-xl font-semibold transition-all">
                      Back to Lesson
                    </button>
                    <button onClick={submitQuiz} disabled={submittingQuiz || Object.keys(quizAnswers).length !== currentLesson.quiz?.questions.length}
                      className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 disabled:bg-gray-300 text-white px-8 py-3 rounded-xl font-bold transition-all">
                      {submittingQuiz ? 'Submitting...' : 'Submit Quiz'}
                    </button>
                  </div>
                </>
              ) : (
                <div className={`text-center p-8 rounded-xl ${quizResult.passed ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                  <div className="text-7xl mb-4">{quizResult.passed ? '🎉' : '😔'}</div>
                  <h3 className={`text-3xl font-bold mb-2 ${quizResult.passed ? 'text-green-700' : 'text-red-700'}`}>
                    {quizResult.passed ? 'Congratulations!' : 'Try Again'}
                  </h3>
                  <p className="text-gray-700 text-xl mb-6">
                    Score: {quizResult.score}% {quizResult.passed ? '(Passed!)' : '(Need 70% to pass)'}
                  </p>
                  <div className="flex gap-4 justify-center flex-wrap">
                    {!quizResult.passed && (
                      <button onClick={() => { setQuizResult(null); setQuizAnswers({}); }}
                        className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-6 py-3 rounded-xl font-bold transition-all">
                        Retry Quiz
                      </button>
                    )}
                    {currentLesson.nextLesson && quizResult.passed && (
                      <button onClick={() => openLesson(currentLesson.nextLesson!.id)}
                        className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white px-6 py-3 rounded-xl font-bold transition-all">
                        Next Lesson →
                      </button>
                    )}
                    <button onClick={() => setView('course')}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-xl font-semibold transition-all">
                      Back to Course
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================================
  // COURSE VIEW
  // ============================================
  if (view === 'course' && currentCourse) {
    const completedCount = currentCourse.lessons.filter(l => isLessonComplete(l.id)).length;
    const progressPercent = Math.round((completedCount / currentCourse.lessons.length) * 100);

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => setView('courses')} className="text-indigo-600 hover:text-indigo-800 mb-6 flex items-center gap-2 font-semibold transition-colors">
            <span>←</span> Back to Courses
          </button>

          <div className="bg-white rounded-2xl border border-gray-200 p-8 mb-8 shadow-lg">
            <span className="text-indigo-500 text-sm font-bold uppercase tracking-wider">{currentCourse.difficulty}</span>
            <h1 className="text-4xl font-bold text-gray-900 mt-2 mb-3">{currentCourse.title}</h1>
            <p className="text-gray-600 text-lg">{currentCourse.description}</p>

            <div className="mt-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2 font-semibold">
                <span>Your Progress</span>
                <span>{completedCount} / {currentCourse.lessons.length} lessons completed</span>
              </div>
              <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-500"
                  style={{width: `${progressPercent}%`}}></div>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Lessons</h2>
          <div className="space-y-4">
            {currentCourse.lessons.map((lesson, idx) => (
              <button key={lesson.id} onClick={() => openLesson(lesson.id)}
                className="w-full bg-white hover:shadow-lg rounded-xl border-2 border-gray-200 hover:border-indigo-300 p-5 text-left transition-all group flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                  isLessonComplete(lesson.id) ? 'bg-gradient-to-br from-green-500 to-emerald-500 text-white' : 'bg-gradient-to-br from-gray-200 to-gray-300 text-gray-700'
                }`}>
                  {isLessonComplete(lesson.id) ? '✓' : idx + 1}
                </div>
                <div className="flex-1">
                  <h3 className="text-gray-900 font-semibold text-lg group-hover:text-indigo-600 transition-colors">{lesson.title}</h3>
                  <p className="text-gray-500 text-sm">Lesson {lesson.lesson_number}</p>
                </div>
                <span className="text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity text-xl">→</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // PUBLICATIONS VIEW
  // ============================================
  if (view === 'publications') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <button onClick={() => setView('home')} className="text-indigo-600 hover:text-indigo-800 mb-6 flex items-center gap-2 font-semibold transition-colors">
            <span>←</span> Back to Home
          </button>

          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-8 md:p-12 mb-8 shadow-2xl text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="text-6xl">📚</div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold">Research Publications</h1>
                <p className="text-indigo-100 text-lg mt-2">Peer-reviewed research and scholarly articles</p>
              </div>
            </div>

            {publicationStats && (
              <div className="flex gap-8 mt-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 px-6">
                  <div className="text-3xl font-bold">{publicationStats.total_publications}</div>
                  <div className="text-indigo-100 text-sm">Publications</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 px-6">
                  <div className="text-3xl font-bold">{publicationStats.total_citations || '0'}</div>
                  <div className="text-indigo-100 text-sm">Total Citations</div>
                </div>
              </div>
            )}
          </div>

          {/* Publications Grid */}
          {publications.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-lg">
              <div className="text-6xl mb-4">📄</div>
              <p className="text-gray-500 text-lg">No publications available yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {publications.map(pub => (
                <div key={pub.id} className="bg-white rounded-2xl border-2 border-gray-200 hover:border-indigo-300 p-6 md:p-8 shadow-lg hover:shadow-xl transition-all">
                  <div className="flex items-start gap-6">
                    <div className="text-5xl flex-shrink-0">{pub.is_featured ? '⭐' : '📄'}</div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{pub.title}</h3>
                      <p className="text-indigo-600 font-semibold mb-1">{pub.authors}</p>
                      <p className="text-gray-600 mb-3">
                        <span className="font-semibold">{pub.venue}</span>
                        {pub.publication_date && (
                          <span className="text-gray-400"> • {new Date(pub.publication_date).getFullYear()}</span>
                        )}
                      </p>

                      <p className="text-gray-700 leading-relaxed mb-4">{pub.abstract}</p>

                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        {pub.tags && pub.tags.length > 0 && pub.tags.map((tag, idx) => (
                          <span key={idx} className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-sm font-medium">
                            {tag}
                          </span>
                        ))}
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        {pub.doi && (
                          <a href={`https://doi.org/${pub.doi}`} target="_blank" rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-800 font-semibold flex items-center gap-1">
                            <span>🔗</span> DOI: {pub.doi}
                          </a>
                        )}
                        {pub.citation_count > 0 && (
                          <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-semibold">
                            📊 {pub.citation_count} citations
                          </span>
                        )}
                        {pub.pdf_url && (
                          <a href={pub.pdf_url} target="_blank" rel="noopener noreferrer"
                            className="bg-red-50 text-red-600 hover:bg-red-100 px-3 py-1 rounded-full font-semibold transition-colors">
                            📥 Download PDF
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================================
  // JOURNAL LANDING PAGE
  // ============================================
  if (view === 'journal') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          <button onClick={() => setView('home')} className="text-blue-600 hover:text-blue-800 mb-6 flex items-center gap-2 font-semibold">
            <span>←</span> Back to Home
          </button>

          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-8 md:p-12 mb-8 shadow-2xl text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="text-6xl">🔬</div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold">Gyanodbhaw Journal</h1>
                <p className="text-blue-100 text-lg mt-2">Submit your research • Peer Review • Publication with DOI</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <button onClick={startNewSubmission}
              className="bg-white rounded-2xl border-2 border-blue-200 hover:border-blue-400 p-8 shadow-lg hover:shadow-xl transition-all group">
              <div className="text-5xl mb-4">📝</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">Submit New Manuscript</h3>
              <p className="text-gray-600">Start a new journal submission with our 7-step wizard</p>
            </button>

            <button onClick={() => { fetchMyManuscripts(); setView('my-manuscripts'); }}
              className="bg-white rounded-2xl border-2 border-indigo-200 hover:border-indigo-400 p-8 shadow-lg hover:shadow-xl transition-all group">
              <div className="text-5xl mb-4">📋</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors">My Manuscripts</h3>
              <p className="text-gray-600">View and manage your submitted manuscripts</p>
            </button>
          </div>

          <div className="mt-8 bg-white rounded-2xl border border-gray-200 p-6 shadow-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-4">About Our Journal</h3>
            <div className="space-y-3 text-gray-700">
              <p>• <strong>Peer-Reviewed:</strong> All submissions undergo rigorous peer review</p>
              <p>• <strong>DOI Registration:</strong> Published articles receive permanent DOI identifiers via Crossref</p>
              <p>• <strong>Open Access:</strong> Research made accessible to the global community</p>
              <p>• <strong>Fast Turnaround:</strong> Average review time of 4-6 weeks</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // MY MANUSCRIPTS VIEW
  // ============================================
  if (view === 'my-manuscripts') {
    const getStatusBadge = (status: string) => {
      const badges: {[key: string]: {bg: string, text: string, label: string}} = {
        'draft': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Draft' },
        'submitted': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Under Review' },
        'under-review': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'In Review' },
        'revision-requested': { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Revisions Requested' },
        'accepted': { bg: 'bg-green-100', text: 'text-green-700', label: 'Accepted' },
        'rejected': { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
        'published': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Published' }
      };
      const badge = badges[status] || badges['draft'];
      return <span className={`${badge.bg} ${badge.text} px-3 py-1 rounded-full text-sm font-semibold`}>{badge.label}</span>;
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          <button onClick={() => setView('journal')} className="text-blue-600 hover:text-blue-800 mb-6 flex items-center gap-2 font-semibold">
            <span>←</span> Back to Journal
          </button>

          <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 shadow-lg mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">My Manuscripts</h2>
            {manuscripts.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📄</div>
                <p className="text-gray-500 text-lg mb-4">You haven't submitted any manuscripts yet.</p>
                <button onClick={startNewSubmission}
                  className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors">
                  Submit Your First Manuscript
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {manuscripts.map(ms => (
                  <div key={ms.id} className="border-2 border-gray-200 rounded-xl p-6 hover:border-blue-300 transition-all">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="text-sm text-gray-500 font-mono mb-1">{ms.manuscript_number}</div>
                        <h3 className="text-xl font-bold text-gray-900">{ms.title}</h3>
                      </div>
                      {getStatusBadge(ms.status)}
                    </div>
                    <p className="text-gray-600 mb-2">{ms.section_name} • {ms.manuscript_type}</p>
                    <p className="text-gray-500 text-sm">Submitted: {new Date(ms.submission_date).toLocaleDateString()}</p>
                    {ms.review_count > 0 && (
                      <p className="text-indigo-600 text-sm mt-2">👥 {ms.review_count} reviews received</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // JOURNAL SUBMISSION WIZARD
  // ============================================
  if (view === 'journal-submit') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => setView('journal')} className="text-blue-600 hover:text-blue-800 mb-6 flex items-center gap-2 font-semibold">
            <span>←</span> Back to Journal
          </button>

          <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 shadow-lg">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-gray-900">Submit Manuscript</h2>
              <div className="text-sm text-gray-500">Step {submissionStep} of 4</div>
            </div>

            {/* Step 1: Manuscript Details */}
            {submissionStep === 1 && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Step 1: Manuscript Information</h3>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Title *</label>
                  <input type="text" value={manuscriptDraft.title || ''}
                    onChange={(e) => setManuscriptDraft({...manuscriptDraft, title: e.target.value})}
                    className="w-full border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none"
                    placeholder="Enter manuscript title" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Abstract *</label>
                  <textarea value={manuscriptDraft.abstract || ''}
                    onChange={(e) => setManuscriptDraft({...manuscriptDraft, abstract: e.target.value})}
                    className="w-full border-2 border-gray-300 rounded-lg p-3 h-40 focus:border-blue-500 focus:outline-none"
                    placeholder="Enter abstract (200-300 words)" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Keywords * (comma-separated)</label>
                  <input type="text"
                    onChange={(e) => setManuscriptDraft({...manuscriptDraft, keywords: e.target.value.split(',').map(k => k.trim())})}
                    className="w-full border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none"
                    placeholder="machine learning, neural networks, deep learning" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Section *</label>
                  <select value={manuscriptDraft.section_id || ''}
                    onChange={(e) => setManuscriptDraft({...manuscriptDraft, section_id: e.target.value})}
                    className="w-full border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none">
                    <option value="">Select a section</option>
                    {journalSections.map(section => (
                      <option key={section.id} value={section.id}>{section.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Manuscript Type *</label>
                  <select value={manuscriptDraft.manuscript_type || 'research-article'}
                    onChange={(e) => setManuscriptDraft({...manuscriptDraft, manuscript_type: e.target.value})}
                    className="w-full border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none">
                    <option value="research-article">Research Article</option>
                    <option value="review">Review Paper</option>
                    <option value="case-study">Case Study</option>
                    <option value="short-communication">Short Communication</option>
                  </select>
                </div>

                <div className="flex justify-end gap-4 pt-6">
                  <button onClick={async () => {
                    const ms = await saveManuscriptDraft();
                    if (ms) setSubmissionStep(2);
                  }}
                    className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors">
                    Save & Continue →
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Authors */}
            {submissionStep === 2 && currentManuscript && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Step 2: Add Authors</h3>
                <p className="text-gray-600 mb-4">Manuscript: <strong>{currentManuscript.manuscript_number}</strong></p>

                <div className="grid grid-cols-2 gap-4">
                  <input type="text" placeholder="First Name *" id="author-first" className="border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none" />
                  <input type="text" placeholder="Last Name *" id="author-last" className="border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none" />
                  <input type="email" placeholder="Email *" id="author-email" className="col-span-2 border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none" />
                  <input type="text" placeholder="Affiliation" id="author-affiliation" className="col-span-2 border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none" />
                  <input type="text" placeholder="Department" id="author-dept" className="border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none" />
                  <input type="text" placeholder="Country" id="author-country" className="border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none" />
                  <input type="text" placeholder="ORCID (optional)" id="author-orcid" className="col-span-2 border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none" />
                  <label className="col-span-2 flex items-center gap-2">
                    <input type="checkbox" id="author-corresponding" className="w-5 h-5" />
                    <span className="text-gray-700">Corresponding Author</span>
                  </label>
                </div>

                <button onClick={async () => {
                  const first = (document.getElementById('author-first') as HTMLInputElement).value;
                  const last = (document.getElementById('author-last') as HTMLInputElement).value;
                  const email = (document.getElementById('author-email') as HTMLInputElement).value;
                  if (!first || !last || !email) { alert('First name, last name, and email are required'); return; }

                  const author: ManuscriptAuthor = {
                    first_name: first,
                    last_name: last,
                    email: email,
                    affiliation: (document.getElementById('author-affiliation') as HTMLInputElement).value,
                    department: (document.getElementById('author-dept') as HTMLInputElement).value,
                    country: (document.getElementById('author-country') as HTMLInputElement).value,
                    orcid: (document.getElementById('author-orcid') as HTMLInputElement).value,
                    is_corresponding: (document.getElementById('author-corresponding') as HTMLInputElement).checked,
                    contribution_statement: '',
                    author_order: draftAuthors.length + 1
                  };

                  const success = await addAuthorToManuscript(author);
                  if (success) {
                    setDraftAuthors([...draftAuthors, author]);
                    // Clear form
                    (document.getElementById('author-first') as HTMLInputElement).value = '';
                    (document.getElementById('author-last') as HTMLInputElement).value = '';
                    (document.getElementById('author-email') as HTMLInputElement).value = '';
                    (document.getElementById('author-affiliation') as HTMLInputElement).value = '';
                    (document.getElementById('author-dept') as HTMLInputElement).value = '';
                    (document.getElementById('author-country') as HTMLInputElement).value = '';
                    (document.getElementById('author-orcid') as HTMLInputElement).value = '';
                    (document.getElementById('author-corresponding') as HTMLInputElement).checked = false;
                  }
                }}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors">
                  + Add Author
                </button>

                {draftAuthors.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-semibold mb-2">Added Authors ({draftAuthors.length}):</h4>
                    <div className="space-y-2">
                      {draftAuthors.map((author, idx) => (
                        <div key={idx} className="bg-gray-50 p-3 rounded-lg">
                          <strong>{author.first_name} {author.last_name}</strong> • {author.email}
                          {author.is_corresponding && <span className="ml-2 text-blue-600">(Corresponding)</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-between gap-4 pt-6">
                  <button onClick={() => setSubmissionStep(1)} className="border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50">
                    ← Back
                  </button>
                  <button onClick={() => draftAuthors.length > 0 ? setSubmissionStep(3) : alert('Please add at least one author')}
                    className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors">
                    Continue →
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: File Upload */}
            {submissionStep === 3 && currentManuscript && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Step 3: Upload Files</h3>
                <p className="text-gray-600 mb-4">Manuscript: <strong>{currentManuscript.manuscript_number}</strong></p>

                <div className="space-y-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                    <div className="text-4xl mb-3">📄</div>
                    <h4 className="font-semibold text-gray-800 mb-2">Manuscript PDF (Required)</h4>
                    <input type="file" accept=".pdf" onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        uploadManuscriptFile(e.target.files[0], 'manuscript-pdf');
                      }
                    }} className="mt-2" />
                    <p className="text-sm text-gray-500 mt-2">PDF format, max 50MB</p>
                  </div>

                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                    <div className="text-4xl mb-3">📊</div>
                    <h4 className="font-semibold text-gray-800 mb-2">Figures & Tables (Optional)</h4>
                    <input type="file" accept=".png,.jpg,.jpeg,.tiff" multiple onChange={(e) => {
                      if (e.target.files) {
                        Array.from(e.target.files).forEach(file => {
                          uploadManuscriptFile(file, 'figure');
                        });
                      }
                    }} className="mt-2" />
                    <p className="text-sm text-gray-500 mt-2">PNG, JPG, TIFF formats</p>
                  </div>

                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                    <div className="text-4xl mb-3">📎</div>
                    <h4 className="font-semibold text-gray-800 mb-2">Supplementary Files (Optional)</h4>
                    <input type="file" multiple onChange={(e) => {
                      if (e.target.files) {
                        Array.from(e.target.files).forEach(file => {
                          uploadManuscriptFile(file, 'supplementary');
                        });
                      }
                    }} className="mt-2" />
                    <p className="text-sm text-gray-500 mt-2">Additional data, code, or supporting documents</p>
                  </div>
                </div>

                {uploadingFile && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                    <p className="text-blue-700 font-semibold">Uploading file...</p>
                  </div>
                )}

                <div className="flex justify-between gap-4 pt-6">
                  <button onClick={() => setSubmissionStep(2)} className="border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50">
                    ← Back
                  </button>
                  <button onClick={() => setSubmissionStep(4)}
                    className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors">
                    Continue →
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Review & Submit */}
            {submissionStep === 4 && currentManuscript && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Step 4: Review & Submit</h3>

                <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Manuscript Number</h4>
                    <p className="text-gray-900 font-mono">{currentManuscript.manuscript_number}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Title</h4>
                    <p className="text-gray-900">{currentManuscript.title}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Authors</h4>
                    <p className="text-gray-900">{draftAuthors.length} author(s) added</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Keywords</h4>
                    <p className="text-gray-900">{currentManuscript.keywords?.join(', ')}</p>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 text-sm">
                    <strong>Note:</strong> Once submitted, your manuscript will enter the peer review process.
                    You will be notified of editorial decisions via email.
                  </p>
                </div>

                <div className="flex justify-between gap-4 pt-6">
                  <button onClick={() => setSubmissionStep(3)} className="border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50">
                    ← Back
                  </button>
                  <button onClick={submitManuscriptForReview}
                    className="bg-green-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-green-700 transition-colors">
                    Submit for Review 🚀
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // COURSES LIST VIEW
  // ============================================
  if (view === 'courses') {
    const getCourseIcon = (title: string) => {
      if (title.toLowerCase().includes('python') || title.toLowerCase().includes('numerical')) return '🐍';
      if (title.toLowerCase().includes('electro')) return '⚡';
      if (title.toLowerCase().includes('statistical') || title.toLowerCase().includes('mechanics')) return '🔬';
      if (title.toLowerCase().includes('quantum') || title.toLowerCase().includes('physics')) return '⚛️';
      return '📚';
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <button onClick={() => setView('home')} className="text-indigo-600 hover:text-indigo-800 mb-6 flex items-center gap-2 font-semibold transition-colors">
            <span>←</span> Back to Home
          </button>

          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-3xl p-8 md:p-12 mb-8 shadow-2xl text-white">
            <div className="flex items-center gap-4">
              <div className="text-6xl">🎓</div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold">Learning Courses</h1>
                <p className="text-blue-100 text-lg mt-2">Comprehensive courses to expand your knowledge</p>
              </div>
            </div>
          </div>

          {/* Courses Grid */}
          {courses.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-lg">
              <div className="text-6xl mb-4">📖</div>
              <p className="text-gray-500 text-lg">No courses available yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map(course => (
                <div key={course.id}
                  className="bg-white rounded-2xl border-2 border-gray-200 hover:border-blue-400 overflow-hidden hover:shadow-xl transition-all group cursor-pointer"
                  onClick={() => openCourse(course.id)}>
                  <div className="h-40 bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                    <span className="text-7xl">{getCourseIcon(course.title)}</span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className={`text-xs px-3 py-1 rounded-full font-bold ${
                        course.difficulty === 'advanced' ? 'bg-red-100 text-red-700' :
                        course.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>{course.difficulty.toUpperCase()}</span>
                      <span className="text-xs text-gray-500 font-semibold">{course.lesson_count} lessons</span>
                      <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded font-bold">FREE</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">{course.title}</h3>
                    <p className="text-gray-600 text-sm line-clamp-2">{course.description}</p>
                    <button className="mt-4 w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold py-3 rounded-xl transition-all shadow-md">
                      Start Learning
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================================
  // CERTIFICATIONS VIEW
  // ============================================
  if (view === 'certifications') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <button onClick={() => setView('home')} className="text-indigo-600 hover:text-indigo-800 mb-6 flex items-center gap-2 font-semibold transition-colors">
            <span>←</span> Back to Home
          </button>

          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-3xl p-8 md:p-12 mb-8 shadow-2xl text-white">
            <div className="flex items-center gap-4">
              <div className="text-6xl">🏆</div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold">Professional Certifications</h1>
                <p className="text-amber-100 text-lg mt-2">Earn recognized certifications for your achievements</p>
              </div>
            </div>
          </div>

          {/* My Certifications */}
          {userCertifications.length > 0 && (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">My Certifications</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {userCertifications.map(cert => (
                  <div key={cert.id} className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-2xl p-6 shadow-lg">
                    <div className="flex items-start gap-4">
                      <div className="text-5xl">🏆</div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900">{cert.title}</h3>
                        <p className="text-gray-600 text-sm mb-2">{cert.course_title}</p>
                        <p className="text-amber-700 text-sm font-bold">Certificate #: {cert.certificate_number}</p>
                        <p className="text-gray-500 text-xs mt-1">Earned: {new Date(cert.earned_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Available Certifications */}
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Available Certifications</h2>
          {certifications.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center shadow-lg">
              <div className="text-6xl mb-4">📜</div>
              <p className="text-gray-500 text-lg">No certifications available yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {certifications.map(cert => {
                const earned = userCertifications.some(uc => uc.title === cert.title);
                return (
                  <div key={cert.id}
                    className={`rounded-2xl border-2 overflow-hidden transition-all shadow-lg hover:shadow-xl ${
                      earned ? 'bg-green-50 border-green-400' : 'bg-white border-gray-200 hover:border-amber-400'
                    }`}>
                    <div className={`h-32 flex items-center justify-center ${
                      earned ? 'bg-gradient-to-br from-green-500 to-emerald-500' : 'bg-gradient-to-br from-amber-500 to-orange-500'
                    }`}>
                      <span className="text-6xl">{earned ? '✅' : '📜'}</span>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`text-xs px-3 py-1 rounded-full font-bold ${
                          cert.difficulty === 'advanced' ? 'bg-red-100 text-red-700' :
                          cert.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>{cert.difficulty.toUpperCase()}</span>
                        <span className="text-sm text-amber-600 font-bold">{cert.price_ahn} AHN</span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{cert.title}</h3>
                      <p className="text-gray-600 text-sm mb-2">{cert.course_title}</p>
                      <p className="text-gray-500 text-xs line-clamp-2 mb-4">{cert.description}</p>
                      {earned ? (
                        <div className="w-full bg-green-100 text-green-700 font-bold py-3 rounded-xl text-center">
                          ✓ Earned
                        </div>
                      ) : (
                        <button onClick={() => purchaseCertification(cert.id)}
                          disabled={purchasingCert === cert.id}
                          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:bg-gray-300 text-white font-bold py-3 rounded-xl transition-all shadow-md">
                          {purchasingCert === cert.id ? 'Processing...' : `Get Certificate`}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ========================================
  // KNOWLEDGE TOKEN CATALOG VIEW
  // ========================================
  if (view === 'tokens') {
    const filteredTokens = knowledgeTokens.filter(t => {
      if (selectedTokenDomain !== 'all' && t.domain !== selectedTokenDomain) return false;
      if (selectedTokenTier > 0 && t.tier !== selectedTokenTier) return false;
      return true;
    });

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-indigo-950 to-purple-950 text-white">
        <div className="w-full px-4 md:px-8 py-8">
          <button onClick={() => setView('home')} className="text-indigo-300 hover:text-indigo-100 mb-6 flex items-center gap-2 font-semibold transition-colors">
            <span>&larr;</span> Back to Home
          </button>

          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <h1 className="text-4xl font-black mb-2 bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-cyan-400">
                Knowledge Tokens
              </h1>
              <p className="text-gray-400">Atomic units of knowledge. Acquire them by proving understanding.</p>
            </div>
            {isAuthenticated && (
              <div className="flex gap-3">
                <button onClick={() => { fetchMyTokens(); fetchMastery(); setView('my-knowledge'); }}
                  className="bg-indigo-600 hover:bg-indigo-500 px-5 py-2 rounded-lg font-bold transition-colors">
                  My Knowledge
                </button>
                <button onClick={() => { fetchReactions(); setView('reactions'); }}
                  className="bg-purple-600 hover:bg-purple-500 px-5 py-2 rounded-lg font-bold transition-colors">
                  Reactions
                </button>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-8">
            <button onClick={() => setSelectedTokenDomain('all')}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${selectedTokenDomain === 'all' ? 'bg-white text-gray-900' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}>
              All Domains
            </button>
            {tokenDomains.map(d => (
              <button key={d.domain} onClick={() => setSelectedTokenDomain(d.domain)}
                className={`px-4 py-2 rounded-lg font-semibold text-sm capitalize transition-colors ${selectedTokenDomain === d.domain ? 'bg-white text-gray-900' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}>
                {d.domain} ({d.token_count})
              </button>
            ))}
            <div className="w-px bg-white/20 mx-2" />
            <button onClick={() => setSelectedTokenTier(0)}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${selectedTokenTier === 0 ? 'bg-white text-gray-900' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}>
              All Tiers
            </button>
            {[1,2,3,4,5].map(t => (
              <button key={t} onClick={() => setSelectedTokenTier(t)}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${selectedTokenTier === t ? 'bg-white text-gray-900' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}>
                T{t}
              </button>
            ))}
          </div>

          {/* Token Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredTokens.map(t => {
              const acquired = isTokenAcquired(t.id);
              return (
                <button key={t.id} onClick={() => fetchTokenDetail(t.id)}
                  className={`relative text-left rounded-2xl p-5 border transition-all hover:scale-[1.02] hover:shadow-xl ${acquired ? 'bg-emerald-900/40 border-emerald-500/50' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
                  {acquired && (
                    <div className="absolute top-3 right-3 bg-emerald-500 text-white text-xs px-2 py-1 rounded-full font-bold">ACQUIRED</div>
                  )}
                  <div className={`inline-block px-2 py-0.5 rounded text-xs font-bold mb-3 bg-gradient-to-r ${tierColors[t.tier]} text-white`}>
                    TIER {t.tier} &middot; {tierNames[t.tier]}
                  </div>
                  <div className="text-2xl font-mono font-bold text-cyan-300 mb-2">{t.symbol}</div>
                  <h3 className="text-lg font-bold text-white mb-2">{t.title}</h3>
                  <p className="text-gray-400 text-sm line-clamp-2 mb-3">{t.description}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="capitalize">{t.domain}</span>
                    <span>Mass: {t.mass}</span>
                    <span className="text-purple-400">+{t.urja_reward} URJA</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ========================================
  // TOKEN DETAIL VIEW
  // ========================================
  if (view === 'token-detail' && currentToken) {
    const acquired = isTokenAcquired(currentToken.id);
    const hasPrereqs = !currentToken.prerequisites?.length || currentToken.prerequisites.every(p => isTokenAcquired(p));

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-indigo-950 to-purple-950 text-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
          <button onClick={() => setView('tokens')} className="text-indigo-300 hover:text-indigo-100 mb-6 flex items-center gap-2 font-semibold transition-colors">
            <span>&larr;</span> Back to Token Catalog
          </button>

          {/* Token Header */}
          <div className={`rounded-2xl p-8 border mb-8 ${acquired ? 'bg-emerald-900/30 border-emerald-500/40' : 'bg-white/5 border-white/10'}`}>
            <div className="flex items-start justify-between mb-4">
              <div className={`inline-block px-3 py-1 rounded-lg text-sm font-bold bg-gradient-to-r ${tierColors[currentToken.tier]} text-white`}>
                TIER {currentToken.tier} &middot; {tierNames[currentToken.tier]}
              </div>
              {acquired && (
                <div className="bg-emerald-500 text-white px-4 py-2 rounded-lg font-bold">ACQUIRED</div>
              )}
            </div>
            <div className="text-4xl md:text-5xl font-mono font-black text-cyan-300 mb-3">{currentToken.symbol}</div>
            <h1 className="text-3xl font-bold text-white mb-2">{currentToken.title}</h1>
            <p className="text-gray-300 text-lg mb-4">{currentToken.description}</p>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="bg-white/10 px-3 py-1 rounded capitalize">{currentToken.domain}</span>
              <span className="bg-white/10 px-3 py-1 rounded">Mass: {currentToken.mass}</span>
              <span className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded">+{currentToken.urja_reward} URJA</span>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-6">
            {currentToken.content.equation && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-bold text-cyan-400 mb-3">Equation</h3>
                <div className="text-2xl text-white bg-black/30 p-4 rounded-lg text-center"><Latex math={currentToken.content.equation} display={true} /></div>
              </div>
            )}

            {currentToken.content.explanation && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-bold text-emerald-400 mb-3">Explanation</h3>
                <p className="text-gray-300 leading-relaxed">{currentToken.content.explanation}</p>
              </div>
            )}

            {currentToken.content.key_insight && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
                <h3 className="text-lg font-bold text-amber-400 mb-3">Key Insight</h3>
                <p className="text-amber-100 leading-relaxed">{currentToken.content.key_insight}</p>
              </div>
            )}

            {currentToken.content.derivation && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-bold text-purple-400 mb-3">Derivation</h3>
                <p className="text-gray-300 font-mono text-sm leading-relaxed">{currentToken.content.derivation}</p>
              </div>
            )}

            {(currentToken.content.units || currentToken.content.constants) && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-bold text-blue-400 mb-3">Reference</h3>
                {currentToken.content.units && <p className="text-gray-300 mb-1"><span className="text-gray-500">Units:</span> {currentToken.content.units}</p>}
                {currentToken.content.constants && <p className="text-gray-300"><span className="text-gray-500">Constants:</span> {currentToken.content.constants}</p>}
                {currentToken.content.rules && <p className="text-gray-300 mt-1"><span className="text-gray-500">Rules:</span> {currentToken.content.rules}</p>}
              </div>
            )}
          </div>

          {/* Prerequisites */}
          {currentToken.prerequisite_details && currentToken.prerequisite_details.length > 0 && (
            <div className="mt-8 bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-bold text-orange-400 mb-4">Prerequisites</h3>
              <div className="flex flex-wrap gap-3">
                {currentToken.prerequisite_details.map(p => (
                  <button key={p.id} onClick={() => fetchTokenDetail(p.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${isTokenAcquired(p.id) ? 'bg-emerald-900/30 border-emerald-500/40 text-emerald-300' : 'bg-red-900/20 border-red-500/30 text-red-300'}`}>
                    <span className="font-mono font-bold">{p.symbol}</span>
                    <span className="text-sm opacity-75">{p.title}</span>
                    {isTokenAcquired(p.id) ? <span className="text-emerald-400 text-xs font-bold ml-1">OK</span> : <span className="text-red-400 text-xs font-bold ml-1">NEEDED</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quiz / Acquire */}
          {!acquired && isAuthenticated && (
            <div className="mt-8 bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-bold text-cyan-400 mb-4">Prove Your Understanding</h3>

              {!hasPrereqs ? (
                <div className="text-red-400 bg-red-900/20 border border-red-500/30 rounded-lg p-4">
                  You need to acquire all prerequisites before attempting this token.
                </div>
              ) : (
                <>
                  {tokenQuizResult && (
                    <div className={`mb-6 p-4 rounded-lg border ${tokenQuizResult.acquired ? 'bg-emerald-900/30 border-emerald-500/40 text-emerald-300' : 'bg-red-900/20 border-red-500/30 text-red-300'}`}>
                      <p className="font-bold text-lg">{tokenQuizResult.message}</p>
                      <p className="text-sm mt-1">Score: {tokenQuizResult.score}% ({tokenQuizResult.correct}/{tokenQuizResult.total})</p>
                    </div>
                  )}

                  {currentToken.quiz_schema.map((q, idx) => (
                    <div key={idx} className="mb-6">
                      <p className="text-white font-semibold mb-3">{idx + 1}. {q.question}</p>
                      <div className="space-y-2">
                        {q.options.map((opt, optIdx) => (
                          <button key={optIdx}
                            onClick={() => setTokenQuizAnswers(prev => ({ ...prev, [idx]: optIdx }))}
                            className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${tokenQuizAnswers[idx] === optIdx ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}>
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}

                  <button
                    onClick={() => handleAcquireToken(currentToken.id)}
                    disabled={acquiringToken || Object.keys(tokenQuizAnswers).length < currentToken.quiz_schema.length}
                    className="w-full bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 disabled:opacity-40 text-white font-bold py-4 rounded-xl text-lg transition-all">
                    {acquiringToken ? 'Validating...' : `Acquire ${currentToken.symbol} (+${currentToken.urja_reward} URJA)`}
                  </button>
                </>
              )}
            </div>
          )}

          {!isAuthenticated && (
            <div className="mt-8 text-center">
              <button onClick={() => setView('login')} className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-8 py-4 rounded-xl text-lg transition-colors">
                Login to Acquire Knowledge Tokens
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ========================================
  // MY KNOWLEDGE VIEW
  // ========================================
  if (view === 'my-knowledge') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-indigo-950 to-purple-950 text-white">
        <div className="w-full px-4 md:px-8 py-8">
          <button onClick={() => setView('tokens')} className="text-indigo-300 hover:text-indigo-100 mb-6 flex items-center gap-2 font-semibold transition-colors">
            <span>&larr;</span> Back to Token Catalog
          </button>

          <h1 className="text-4xl font-black mb-2 bg-clip-text text-transparent bg-gradient-to-r from-amber-400 to-orange-400">
            My Knowledge Portfolio
          </h1>
          <p className="text-gray-400 mb-8">Your acquired knowledge tokens and domain mastery.</p>

          {/* Mastery Overview */}
          {masteryTotals && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
                <div className="text-3xl font-black text-cyan-400">{masteryTotals.tokens_acquired}</div>
                <div className="text-gray-400 text-sm">Tokens Acquired</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
                <div className="text-3xl font-black text-emerald-400">{masteryTotals.total_mass.toFixed(1)}</div>
                <div className="text-gray-400 text-sm">Total Mass</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
                <div className="text-3xl font-black text-purple-400">{masteryTotals.tokens_available}</div>
                <div className="text-gray-400 text-sm">Total Available</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
                <div className="text-3xl font-black text-amber-400">
                  {masteryTotals.tokens_available > 0 ? Math.round((masteryTotals.tokens_acquired / masteryTotals.tokens_available) * 100) : 0}%
                </div>
                <div className="text-gray-400 text-sm">Completion</div>
              </div>
            </div>
          )}

          {/* Domain Mastery Cards */}
          {domainMastery.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4">Domain Mastery</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {domainMastery.map(dm => (
                  <div key={dm.domain} className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold capitalize">{dm.domain}</h3>
                      <span className="text-cyan-400 font-mono font-bold">Level {parseFloat(dm.mastery_level).toFixed(2)}</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-3 mb-4">
                      <div className="bg-gradient-to-r from-cyan-500 to-emerald-500 h-3 rounded-full transition-all" style={{width: `${Math.min(parseFloat(dm.mastery_level) * 10, 100)}%`}} />
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded">T1: {dm.tier_1_count}</span>
                      <span className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded">T2: {dm.tier_2_count}</span>
                      <span className="bg-purple-500/20 text-purple-300 px-2 py-1 rounded">T3: {dm.tier_3_count}</span>
                      <span className="bg-orange-500/20 text-orange-300 px-2 py-1 rounded">T4: {dm.tier_4_count}</span>
                      <span className="bg-pink-500/20 text-pink-300 px-2 py-1 rounded">T5: {dm.tier_5_count}</span>
                      <span className="bg-white/10 text-gray-300 px-2 py-1 rounded ml-auto">Mass: {parseFloat(dm.total_mass).toFixed(1)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Acquired Tokens List */}
          <h2 className="text-2xl font-bold mb-4">Acquired Tokens ({myTokens.length})</h2>
          {myTokens.length === 0 ? (
            <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center">
              <p className="text-gray-400 text-lg mb-4">You haven't acquired any knowledge tokens yet.</p>
              <button onClick={() => setView('tokens')} className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-6 py-3 rounded-lg transition-colors">
                Browse Token Catalog
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myTokens.map(t => (
                <button key={t.id} onClick={() => fetchTokenDetail(t.token_id)}
                  className="text-left bg-emerald-900/30 border border-emerald-500/30 rounded-xl p-5 hover:border-emerald-400/50 transition-all">
                  <div className={`inline-block px-2 py-0.5 rounded text-xs font-bold mb-2 bg-gradient-to-r ${tierColors[t.tier]} text-white`}>
                    TIER {t.tier}
                  </div>
                  <div className="text-xl font-mono font-bold text-cyan-300 mb-1">{t.symbol}</div>
                  <h3 className="text-white font-bold mb-1">{t.title}</h3>
                  <div className="flex items-center justify-between text-xs text-gray-400 mt-3">
                    <span className="capitalize">{t.domain}</span>
                    <span>Score: {t.quiz_score}%</span>
                    <span>{new Date(t.acquired_at).toLocaleDateString()}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ========================================
  // KNOWLEDGE REACTIONS VIEW
  // ========================================
  if (view === 'reactions') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-indigo-950 to-purple-950 text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
          <button onClick={() => setView('tokens')} className="text-indigo-300 hover:text-indigo-100 mb-6 flex items-center gap-2 font-semibold transition-colors">
            <span>&larr;</span> Back to Token Catalog
          </button>

          <h1 className="text-4xl font-black mb-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">
            Knowledge Reactions
          </h1>
          <p className="text-gray-400 mb-8">Combine acquired tokens to synthesize higher-tier knowledge. Like nuclear fusion for the mind.</p>

          {knowledgeReactions.length === 0 ? (
            <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center">
              <p className="text-gray-400">Loading reactions...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {knowledgeReactions.map(r => (
                <div key={r.id} className={`rounded-xl p-6 border transition-all ${r.already_have_output ? 'bg-emerald-900/20 border-emerald-500/30' : r.can_execute ? 'bg-indigo-900/30 border-indigo-500/40' : 'bg-white/5 border-white/10 opacity-60'}`}>
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Inputs */}
                    <div className="flex flex-wrap items-center gap-2 flex-1">
                      {r.input_details.map((inp, i) => (
                        <React.Fragment key={inp.id}>
                          <div className={`px-3 py-2 rounded-lg border font-mono text-sm ${isTokenAcquired(inp.id) ? 'bg-emerald-900/30 border-emerald-500/40 text-emerald-300' : 'bg-red-900/20 border-red-500/30 text-red-400'}`}>
                            {inp.symbol}
                          </div>
                          {i < r.input_details.length - 1 && <span className="text-gray-500 font-bold">+</span>}
                        </React.Fragment>
                      ))}
                      <span className="text-gray-400 font-bold text-xl mx-2">&rarr;</span>
                      <div className="px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500/20 to-emerald-500/20 border border-cyan-500/40 text-cyan-300 font-mono font-bold">
                        {r.output_symbol}
                      </div>
                    </div>

                    {/* Info + Action */}
                    <div className="flex items-center gap-3">
                      <div className="text-xs text-right">
                        {parseFloat(r.urja_cost) > 0 && <div className="text-red-400">-{r.urja_cost} URJA</div>}
                        {parseFloat(r.urja_reward) > 0 && <div className="text-emerald-400">+{r.urja_reward} URJA</div>}
                      </div>
                      {r.already_have_output ? (
                        <div className="bg-emerald-500/20 text-emerald-300 px-4 py-2 rounded-lg text-sm font-bold">Completed</div>
                      ) : r.can_execute ? (
                        <button onClick={() => handleExecuteReaction(r.id)}
                          disabled={executingReaction === r.id}
                          className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white px-5 py-2 rounded-lg font-bold text-sm transition-all disabled:opacity-50">
                          {executingReaction === r.id ? 'Fusing...' : 'Fuse'}
                        </button>
                      ) : (
                        <div className="text-gray-500 text-sm font-semibold">Missing inputs</div>
                      )}
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm mt-3">{r.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Handle authentication requirement for protected views
  const handleProtectedAction = (action: () => void) => {
    if (!isAuthenticated) {
      // Show login prompt
      setShowRegister(false);
      setView('login');
      return;
    }
    action();
  };

  // Show login/register modal if view is set to login/register
  if (view === 'login') {
    if (showRegister) {
      return <Register onSwitchToLogin={() => setShowRegister(false)} />;
    }
    return <Login onSwitchToRegister={() => setShowRegister(true)} />;
  }

  // ============================================
  // HOME VIEW (PUBLIC LANDING + AUTHENTICATED)
  // ============================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="text-5xl md:text-6xl font-black mb-2">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-200 to-pink-200">
                  ज्ञानोदभव
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">GYANODBHAW</h1>
              <p className="text-indigo-100 text-lg">The Seed of Knowledge • University Foundation</p>
            </div>

            {/* Show user wallet if authenticated, or Login/Sign Up buttons if not */}
            {isAuthenticated && user ? (
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <p className="text-white/80 text-sm mb-1">Welcome back,</p>
                <p className="text-white font-bold text-xl">{user.username}</p>
                <div className="flex items-center gap-3 mt-3">
                  <span className="bg-yellow-400 text-yellow-900 px-4 py-2 rounded-lg font-bold text-sm">
                    {user.wallet.ahn.toFixed(0)} AHN
                  </span>
                  <span className="bg-purple-400 text-purple-900 px-4 py-2 rounded-lg font-bold text-sm">
                    {user.wallet.urja.toFixed(0)} URJA
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <button
                  onClick={() => { setShowRegister(false); setView('login'); }}
                  className="bg-white/10 backdrop-blur-md hover:bg-white/20 border-2 border-white/40 text-white px-6 py-3 rounded-xl font-bold transition-all">
                  Login
                </button>
                <button
                  onClick={() => { setShowRegister(true); setView('login'); }}
                  className="bg-white hover:bg-indigo-50 text-indigo-600 px-6 py-3 rounded-xl font-bold shadow-lg transition-all">
                  Sign Up Free
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content - 80/20 Split Layout */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Explore Our Offerings</h2>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left: Main Content Area (80%) */}
          <div className="flex-1 lg:w-4/5 space-y-6">
            {/* Knowledge Tokens Card — PRIMARY */}
            <button onClick={() => setView('tokens')}
              className="w-full bg-gradient-to-br from-gray-900 via-indigo-950 to-purple-950 rounded-3xl border-2 border-cyan-500/30 hover:border-cyan-400/60 p-8 shadow-xl hover:shadow-2xl hover:shadow-cyan-500/10 transition-all transform hover:scale-105 text-left group">
              <div className="flex items-center gap-4 mb-6">
                <div className="text-6xl">&#9883;</div>
                <div>
                  <h3 className="text-3xl font-bold text-white group-hover:text-cyan-300 transition-colors">
                    Knowledge Tokens
                  </h3>
                  <p className="text-cyan-400 text-sm font-semibold">Tokenized Knowledge for Structured Learning</p>
                </div>
              </div>
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                Atomic units of knowledge — equations, laws, concepts. Acquire tokens by proving understanding, combine them through reactions to unlock higher-tier mastery.
              </p>
              <div className="flex flex-wrap items-center gap-3 text-sm mb-4">
                <div className="bg-emerald-500/20 text-emerald-300 px-4 py-2 rounded-lg font-bold border border-emerald-500/30">
                  {knowledgeTokens.length} Tokens
                </div>
                <div className="bg-purple-500/20 text-purple-300 px-4 py-2 rounded-lg font-bold border border-purple-500/30">
                  {tokenDomains.length} Domains
                </div>
                {masteryTotals && masteryTotals.tokens_acquired > 0 && (
                  <div className="bg-cyan-500/20 text-cyan-300 px-4 py-2 rounded-lg font-bold border border-cyan-500/30">
                    {masteryTotals.tokens_acquired} Acquired
                  </div>
                )}
                <div className="bg-amber-500/20 text-amber-300 px-4 py-2 rounded-lg font-bold border border-amber-500/30">
                  Earn URJA
                </div>
              </div>
              <div className="flex items-center gap-2 text-cyan-400 font-bold group-hover:gap-4 transition-all">
                <span>Explore Knowledge Tokens</span>
                <span className="text-2xl">&rarr;</span>
              </div>
            </button>

            {/* Journal Card */}
            <button onClick={() => handleProtectedAction(() => setView('journal'))}
              className="w-full bg-white rounded-3xl border-2 border-gray-200 hover:border-blue-500 p-8 shadow-xl hover:shadow-2xl transition-all transform hover:scale-105 text-left group">
              <div className="text-7xl mb-6">🔬</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors">
                Journal System
              </h3>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                Submit manuscripts, undergo peer review, and publish your research with DOI registration via Crossref.
              </p>
              <div className="flex items-center gap-4 text-sm">
                <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg font-bold">
                  Submit Research
                </div>
                <div className="bg-green-50 text-green-700 px-4 py-2 rounded-lg font-bold">
                  Get Published
                </div>
              </div>
              <div className="mt-6 flex items-center gap-2 text-blue-600 font-bold group-hover:gap-4 transition-all">
                <span>Open Journal</span>
                <span className="text-2xl">→</span>
              </div>
            </button>

            {/* Publications Card */}
            <button onClick={() => handleProtectedAction(() => setView('publications'))}
              className="w-full bg-white rounded-3xl border-2 border-gray-200 hover:border-indigo-400 p-8 shadow-xl hover:shadow-2xl transition-all transform hover:scale-105 text-left group">
              <div className="text-7xl mb-6">📚</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4 group-hover:text-indigo-600 transition-colors">
                Research Publications
              </h3>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                Explore peer-reviewed research articles, conference papers, and scholarly publications in various scientific domains.
              </p>
              <div className="flex items-center gap-4 text-sm">
                {publicationStats && (
                  <>
                    <div className="bg-indigo-50 text-indigo-700 px-4 py-2 rounded-lg font-bold">
                      {publicationStats.total_publications} Publications
                    </div>
                    <div className="bg-purple-50 text-purple-700 px-4 py-2 rounded-lg font-bold">
                      {publicationStats.total_citations || '0'} Citations
                    </div>
                  </>
                )}
              </div>
              <div className="mt-6 flex items-center gap-2 text-indigo-600 font-bold group-hover:gap-4 transition-all">
                <span>Browse Publications</span>
                <span className="text-2xl">→</span>
              </div>
            </button>
          </div>

          {/* Right: Advertisement Sidebar (20%) */}
          <div className="lg:w-1/5 space-y-6 hidden lg:block">
            {/* Courses Ad */}
            <div className="sticky top-4">
              <button onClick={() => handleProtectedAction(() => setView('courses'))}
                className="w-full bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all text-left group border-2 border-purple-300 hover:border-purple-200 transform hover:scale-105">
                <div className="text-6xl mb-4 text-center">🎓</div>
                <h3 className="text-xl font-bold text-white mb-3 text-center">
                  Learning Courses
                </h3>
                <p className="text-purple-100 text-sm mb-4 leading-relaxed">
                  Master new skills with interactive lessons and quizzes.
                </p>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 mb-4">
                  <p className="text-white font-bold text-center text-sm">
                    {courses.length} Courses Available
                  </p>
                  <p className="text-purple-200 text-xs text-center mt-1">
                    100% FREE
                  </p>
                </div>
                <div className="bg-white text-purple-600 py-2 px-4 rounded-lg font-bold text-center text-sm hover:bg-purple-100 transition-colors">
                  Start Learning →
                </div>
              </button>

              {/* Certifications Ad */}
              <button onClick={() => handleProtectedAction(() => setView('certifications'))}
                className="w-full bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all text-left group border-2 border-amber-300 hover:border-amber-200 transform hover:scale-105 mt-6">
                <div className="text-6xl mb-4 text-center">🏆</div>
                <h3 className="text-xl font-bold text-white mb-3 text-center">
                  Certifications
                </h3>
                <p className="text-amber-100 text-sm mb-4 leading-relaxed">
                  Earn professional certifications and boost your career.
                </p>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 mb-4">
                  <p className="text-white font-bold text-center text-sm">
                    {certifications.length} Certificates
                  </p>
                  {userCertifications.length > 0 && (
                    <p className="text-amber-200 text-xs text-center mt-1">
                      You earned {userCertifications.length}!
                    </p>
                  )}
                </div>
                <div className="bg-white text-amber-600 py-2 px-4 rounded-lg font-bold text-center text-sm hover:bg-amber-100 transition-colors">
                  Get Certified →
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: Show courses/certs at bottom */}
      <div className="lg:hidden max-w-7xl mx-auto px-4 md:px-8 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button onClick={() => handleProtectedAction(() => setView('courses'))}
            className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all text-left">
            <div className="text-6xl mb-4 text-center">🎓</div>
            <h3 className="text-xl font-bold text-white mb-2 text-center">Learning Courses</h3>
            <p className="text-purple-100 text-sm mb-3 text-center">{courses.length} courses available</p>
            <div className="bg-white text-purple-600 py-2 px-4 rounded-lg font-bold text-center text-sm">
              Start Learning →
            </div>
          </button>

          <button onClick={() => handleProtectedAction(() => setView('certifications'))}
            className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all text-left">
            <div className="text-6xl mb-4 text-center">🏆</div>
            <h3 className="text-xl font-bold text-white mb-2 text-center">Certifications</h3>
            <p className="text-amber-100 text-sm mb-3 text-center">{certifications.length} certificates</p>
            <div className="bg-white text-amber-600 py-2 px-4 rounded-lg font-bold text-center text-sm">
              Get Certified →
            </div>
          </button>
        </div>
      </div>

      {/* Back Link */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pb-12">
        <div className="text-center">
          <a href="/" className="text-indigo-600 hover:text-indigo-800 font-semibold transition-colors">
            ← Back to Ahantravyuh Dashboard
          </a>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
